/**
 * Onboarding Cache Manager - Multitenant Safe
 * 
 * Manages onboarding status caching with localStorage fallback
 * for optimal performance and user experience.
 * 
 * SECURITY:
 * - Cache is scoped per user (prevents cross-user leakage)
 * - Validates tenant_id to ensure user still has access
 * - Auto-expires after 7 days for security
 * - Cleared on logout
 */

const CACHE_PREFIX = 'imobpanda_onboarding';
const CACHE_VERSION = '1.1'; // Updated for multitenant support
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

interface OnboardingCache {
  completed: boolean;
  userId: string;
  tenantId: string | null;
  timestamp: number;
  version: string;
}

/**
 * Get cache key scoped to user (prevents cross-user contamination)
 */
const getCacheKey = (userId: string): string => {
  return `${CACHE_PREFIX}_${userId}`;
};

/**
 * Get cached onboarding status with multitenant validation
 */
export const getCachedOnboardingStatus = (
  userId: string,
  currentTenantId?: string | null
): boolean | null => {
  try {
    const cacheKey = getCacheKey(userId);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const data: OnboardingCache = JSON.parse(cached);

    // Validate cache version
    if (data.version !== CACHE_VERSION) {
      clearOnboardingCache(userId);
      return null;
    }

    // Validate user ID
    if (data.userId !== userId) {
      clearOnboardingCache(userId);
      return null;
    }

    // Validate cache TTL (auto-expire after 7 days)
    const age = Date.now() - data.timestamp;
    if (age > CACHE_TTL) {
      clearOnboardingCache(userId);
      return null;
    }

    // Validate tenant_id if provided (multitenant security)
    if (currentTenantId !== undefined && data.tenantId !== currentTenantId) {
      clearOnboardingCache(userId);
      return null;
    }

    // Validate completion status
    if (!data.completed) {
      return null;
    }

    return data.completed;
  } catch (error) {
    console.error('[OnboardingCache] Error reading cache:', error);
    clearOnboardingCache(userId);
    return null;
  }
};

/**
 * Set onboarding status in cache with tenant validation
 */
export const setCachedOnboardingStatus = (
  userId: string,
  completed: boolean,
  tenantId?: string | null
): void => {
  try {
    const cacheKey = getCacheKey(userId);
    const data: OnboardingCache = {
      completed,
      userId,
      tenantId: tenantId || null,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };

    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (error) {
    console.error('[OnboardingCache] Error writing cache:', error);
  }
};

/**
 * Clear onboarding cache for specific user
 */
export const clearOnboardingCache = (userId?: string): void => {
  try {
    if (userId) {
      // Clear specific user cache
      const cacheKey = getCacheKey(userId);
      localStorage.removeItem(cacheKey);
    } else {
      // Clear all onboarding caches (logout scenario)
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    console.error('[OnboardingCache] Error clearing cache:', error);
  }
};
